import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Package } from 'lucide-react'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportInventoryReport,
  fetchInventoryReport,
  formatReportDate,
  formatReportDateTime,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

export function InventoryReportsPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchInventoryReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load inventory report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportInventoryReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const hasActivity =
    (report?.summary?.movement_count ?? 0) > 0 ||
    (report?.stock_on_hand?.length ?? 0) > 0 ||
    (report?.summary?.low_stock_count ?? 0) > 0

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Package className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Inventory</h2>
            <p className="text-sm text-muted-foreground">
              Stock on hand, movements, low stock, consumables usage, and GRN purchases.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="Stock value"
              value={`AED ${formatReportMoney(report.summary.stock_value)}`}
            />
            <SummaryCard label="Low stock SKUs" value={String(report.summary.low_stock_count)} />
            <SummaryCard label="Movements" value={String(report.summary.movement_count)} />
            <SummaryCard
              label="GRN / consumables"
              value={`${report.summary.grn_receipt_count} GRN · ${report.summary.consumables_quantity} units`}
            />
          </div>

          {report.low_stock.length > 0 ? (
            <div className="mb-6 overflow-x-auto rounded-2xl border border-amber-200/80 bg-amber-50/40">
              <p className="border-b border-amber-200/60 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-amber-800">
                Low stock alert
              </p>
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Product</th>
                    <th className="px-4 py-3 font-medium">On hand</th>
                    <th className="px-4 py-3 font-medium">Min</th>
                    <th className="px-4 py-3 font-medium">Shortfall</th>
                  </tr>
                </thead>
                <tbody>
                  {report.low_stock.map((row) => (
                    <tr key={row.product_id} className="border-t border-amber-200/50">
                      <td className="px-4 py-3 font-medium">{row.product_name}</td>
                      <td className="px-4 py-3">{row.quantity_on_hand}</td>
                      <td className="px-4 py-3">{row.min_stock}</td>
                      <td className="px-4 py-3">{row.shortfall}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div className="mb-6 grid gap-6 lg:grid-cols-2">
            <div className="overflow-x-auto rounded-2xl border border-border/70">
              <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Stock on hand
              </p>
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">Product</th>
                    <th className="px-4 py-3 font-medium">Qty</th>
                    <th className="px-4 py-3 font-medium">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {report.stock_on_hand.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">
                        No stock on hand.
                      </td>
                    </tr>
                  ) : (
                    report.stock_on_hand.slice(0, 12).map((row) => (
                      <tr key={row.product_id} className="border-t border-border/60">
                        <td className="px-4 py-3">{row.product_name}</td>
                        <td className="px-4 py-3">{row.quantity_on_hand}</td>
                        <td className="px-4 py-3">AED {formatReportMoney(row.stock_value)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-border/70">
              <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                GRN purchases · {formatReportDate(report.from)} – {formatReportDate(report.to)}
              </p>
              <ul className="divide-y divide-border/60 text-sm">
                {(report.grn_purchases?.receipts ?? []).slice(0, 8).map((row) => (
                  <li key={row.id} className="px-4 py-3">
                    <div className="font-medium">{row.grn_number}</div>
                    <div className="text-muted-foreground">
                      {row.supplier_name ?? '—'} · AED {formatReportMoney(row.total_cost)}
                    </div>
                  </li>
                ))}
                {(report.grn_purchases?.receipts ?? []).length === 0 ? (
                  <li className="px-4 py-6 text-center text-muted-foreground">No GRNs in period.</li>
                ) : null}
              </ul>
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <p className="border-b border-border/60 bg-muted/20 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Movements & consumables
            </p>
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">When</th>
                  <th className="px-4 py-3 font-medium">Product</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Delta</th>
                </tr>
              </thead>
              <tbody>
                {!hasActivity ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">
                      No inventory activity in this period.
                    </td>
                  </tr>
                ) : (
                  report.movements.slice(0, 20).map((row) => (
                    <tr key={row.id} className="border-t border-border/60">
                      <td className="px-4 py-3">{formatReportDateTime(row.created_at)}</td>
                      <td className="px-4 py-3">{row.product_name}</td>
                      <td className="px-4 py-3">{row.movement_type_label}</td>
                      <td className="px-4 py-3">{row.quantity_delta}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
